import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { Button, Panel, PopoverPanel, colors } from 'src/components/app-ui'
import AccessRule from 'src/model/access-rule'
import { collectionMemberFilterAction } from 'src/reducers/teams/action'
import usePathSettings from '../hooks/usePathSettings'
import { tabPath } from '../tabs'
import MembersTable from './members-table'
import { learnerActions } from './learner-actions'

const PAGE_SIZE = 20
const INVITATION_ONLY = 2
const PUBLIC = 0

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  access: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '16px 20px',
  },
  title: {
    fontSize: '15px',
    fontWeight: 600,
  },
  desc: {
    marginTop: 2,
    fontSize: '13px',
    color: colors.muted,
  },
  strong: {
    fontWeight: 600,
    color: colors.navy,
  },
  link: {
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    color: colors.primary,
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  table: {
    position: 'relative',
  },
  tableHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 20px',
    borderBottom: `1px solid ${colors.border}`,
  },
  tableTitle: {
    fontSize: '15px',
    fontWeight: 600,
  },
  bulkButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    background: '#FFFFFF',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12px',
    color: colors.navy,
  },
  menu: {
    display: 'flex',
    flexDirection: 'column',
    padding: '6px',
  },
  menuItem: {
    padding: '8px 10px',
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: '13px',
    color: colors.navy,
    '&:hover': {
      background: colors.hover,
    },
  },
  empty: {
    padding: '40px 20px',
    textAlign: 'center',
    fontSize: '14px',
    color: colors.muted,
  },
  pager: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    fontSize: '13px',
    color: colors.muted,
  },
})

const LearnersTab = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { id } = useParams()
  const { collection, settings } = usePathSettings()
  const memberList = useSelector(state => state.teamsList.memberList)
  const [page, setPage] = useState(0)
  const [bulkOpen, setBulkOpen] = useState(false)
  const closeBulk = useCallback(() => setBulkOpen(false), [])

  const actions = learnerActions(dispatch)
  const accessRule = settings.accessRule ?? collection.accessRule ?? PUBLIC
  const teamsId = settings.teams?.find(team => team.default === true)?.id
  const members = memberList?.results || []
  const total = memberList?.totalCount || 0

  const load = useCallback(
    (pageIndex = page) =>
      dispatch(
        collectionMemberFilterAction(id, {
          CurrentPage: pageIndex,
          PageSize: PAGE_SIZE,
          SortingColumn: 'CreatedAt',
          Asc: false,
          FilterText: '',
          Filters: [],
          Id: id,
          TeamsId: teamsId,
        }),
      ),
    [dispatch, id, teamsId, page],
  )

  useEffect(() => {
    if (accessRule !== PUBLIC) load(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, teamsId, page, accessRule])

  const goToAuth = () =>
    history.push(tabPath(id, 'settings', 'learner-authentication'))

  const bulkItems = [
    { label: 'Import list', run: () => actions.import() },
    { label: 'Export list', run: () => actions.export(teamsId) },
    ...(accessRule === INVITATION_ONLY
      ? [
          {
            label: 'Link from learning path',
            run: () => actions.linkFromPath(id),
          },
        ]
      : []),
  ]

  const from = total === 0 ? 0 : page * PAGE_SIZE + 1
  const to = Math.min(total, (page + 1) * PAGE_SIZE)

  return (
    <div className={classes.root}>
      <Panel className={classes.access}>
        <div>
          <div className={classes.title}>Who can access this path</div>
          <div className={classes.desc}>
            Access is{' '}
            <strong className={classes.strong}>{AccessRule[accessRule]}</strong>
            .{' '}
            <button type="button" className={classes.link} onClick={goToAuth}>
              Change access
            </button>
          </div>
        </div>
        {accessRule !== PUBLIC && (
          <div className={classes.actions}>
            <Button
              variant="outline"
              size="small"
              icon="tabler:user-plus"
              onClick={() => actions.add(() => load())}
            >
              Add learner
            </Button>
            {accessRule === INVITATION_ONLY && (
              <Button
                size="small"
                icon="tabler:mail"
                onClick={actions.inviteByEmail}
              >
                Invite by email
              </Button>
            )}
          </div>
        )}
      </Panel>

      <Panel className={classes.table}>
        <div className={classes.tableHead}>
          <span className={classes.tableTitle}>
            Enrolled learners{accessRule !== PUBLIC ? ` · ${total}` : ''}
          </span>
          {accessRule !== PUBLIC && (
            <PopoverPanel
              open={bulkOpen}
              onClose={closeBulk}
              width={200}
              align="right"
              panelStyle={{ top: 36 }}
              trigger={
                <button
                  type="button"
                  className={classes.bulkButton}
                  onClick={() => setBulkOpen(value => !value)}
                >
                  Bulk actions
                  <Icon icon="tabler:chevron-down" width={12} height={12} />
                </button>
              }
            >
              <div className={classes.menu}>
                {bulkItems.map(item => (
                  <button
                    key={item.label}
                    type="button"
                    className={classes.menuItem}
                    onClick={() => {
                      closeBulk()
                      item.run()
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </PopoverPanel>
          )}
        </div>

        {accessRule === PUBLIC ? (
          <div className={classes.empty}>
            Anyone with the link can open this path, so there is no learner
            list. See Analytics for engagement.
          </div>
        ) : members.length === 0 ? (
          <div className={classes.empty}>No learners yet.</div>
        ) : (
          <MembersTable
            members={members}
            onEdit={actions.edit}
            onDelete={actions.remove}
          />
        )}

        {total > PAGE_SIZE && (
          <div className={classes.pager}>
            <span>
              {from}–{to} of {total}
            </span>
            <div className={classes.actions}>
              <Button
                variant="secondary"
                size="small"
                icon="tabler:chevron-left"
                disabled={page === 0}
                onClick={() => setPage(value => value - 1)}
              />
              <Button
                variant="secondary"
                size="small"
                icon="tabler:chevron-right"
                disabled={to >= total}
                onClick={() => setPage(value => value + 1)}
              />
            </div>
          </div>
        )}
      </Panel>
    </div>
  )
}

export default LearnersTab
